// src/private/src/pages/app/reports/DailyPulse.jsx
import * as React from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  CircularProgress,
  TableSortLabel,
  TablePagination,
  FormControl,
  FormControlLabel,
  InputLabel,
  Button,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Tooltip,
  TextField,
  Skeleton,
  IconButton,
  Popover,
  InputAdornment,
  Alert,
} from '@mui/material';
import {
  Refresh,
  Search as SearchIcon,
  ViewColumn as ViewColumnIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Close as CloseIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { LinkSimple } from '@phosphor-icons/react';

import {
  DateRangePicker,
  DollarFormatter,
  PercentageFormatter
} from '@src/components';
import { useDailyPulse } from '@src/context';
import { RouterLink } from '@src/components/core/link';
import { CLIENT_STATUS_OPTIONS, DEFAULT_CLIENT_STATUS_FILTER } from '@src/constants/clients';
import api from '@src/api/clients';

const MULTI_SELECT_MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 48 * 4.5 + 8,
      width: 280,
    },
  },
};

const STATUS_LABEL_LOOKUP = CLIENT_STATUS_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const PRIORITY_CLIENT_STATUSES = ['onboarding', 'active_new'];
const PRIORITY_CLIENT_STATUS_SET = new Set(PRIORITY_CLIENT_STATUSES);
const PRIORITY_STATUS_ORDER = PRIORITY_CLIENT_STATUSES.reduce((acc, status, index) => {
  acc[status] = index;
  return acc;
}, {});
const PURPLE_STATUS_COLOR = '#7c4dff';
const PURPLE_STATUS_COLOR_DARK = '#c38aff';

const formatLaunchDate = (value) => {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return null;
  }
  return parsed.format('MM/DD');
};

const getClientLaunchDateLabel = (status, launchDate) => {
  if (!PRIORITY_CLIENT_STATUS_SET.has(status)) {
    return null;
  }
  return formatLaunchDate(launchDate);
};

const toFiniteNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const safeRatio = (numerator, ...denominatorParts) => {
  const num = Number(numerator);
  if (!Number.isFinite(num)) {
    return null;
  }

  let denominator = 0;
  for (const part of denominatorParts) {
    const value = Number(part);
    if (!Number.isFinite(value)) {
      return null;
    }
    denominator += value;
  }

  if (!Number.isFinite(denominator) || denominator === 0) {
    return null;
  }

  const ratio = num / denominator;
  return Number.isFinite(ratio) ? ratio : null;
};

const safeAlpha = (color, opacity) => {
  if (typeof alpha === 'function' && typeof color === 'string') {
    try {
      return alpha(color, opacity);
    } catch {
      // If alpha application fails fall back to the base color.
    }
  }

  return color;
};

const toPositiveNumber = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const shouldDisplayClientRow = (row) => {
  if (!row) {
    return false;
  }

  if (toPositiveNumber(row.total_spend_sum ?? row.total_spend)) {
    return true;
  }

  if (toPositiveNumber(row.budget_amount ?? row.budget)) {
    return true;
  }

  if (toPositiveNumber(row.facebook_daily_budget_total ?? row.facebook_budget_amount)) {
    return true;
  }

  return false;
};

const parseRatioValue = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const getMarketingSeverity = (value, goal) => {
  const metric = parseRatioValue(value);
  const target = parseRatioValue(goal);
  if (metric === null || target === null) {
    return null;
  }

  if (metric <= target) {
    return 'low';
  }

  const mediumThreshold = target * 1.15;
  if (metric <= mediumThreshold) {
    return 'medium';
  }
  return 'high';
};

const marketingSeverityStyles = {
  low: (theme) => ({
    color: theme.palette.success.dark,
    backgroundColor: safeAlpha(theme.palette.success.light || theme.palette.success.main, 0.35),
    borderColor: safeAlpha(theme.palette.success.main, 0.5),
  }),
  medium: (theme) => ({
    color: theme.palette.warning.dark || theme.palette.warning.main,
    backgroundColor: safeAlpha(theme.palette.warning.light || theme.palette.warning.main, 0.35),
    borderColor: safeAlpha(theme.palette.warning.main, 0.45),
  }),
  high: (theme) => ({
    color: theme.palette.error.dark,
    backgroundColor: safeAlpha(theme.palette.error.light || theme.palette.error.main, 0.35),
    borderColor: safeAlpha(theme.palette.error.main, 0.45),
  }),
};

const CostOfMarketingPill = ({ value, goal }) => {
  const severity = getMarketingSeverity(value, goal);
  const display = <PercentageFormatter value={value} />;

  if (!severity) {
    return display;
  }

  return (
    <Box
      component="span"
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: '0.85rem',
        px: 1,
        py: 0.25,
        borderRadius: theme.shape.borderRadius * 2,
        border: '1px solid',
        minWidth: theme.spacing(8),
        ...marketingSeverityStyles[severity](theme),
      })}
    >
      {display}
    </Box>
  );
};

const INGESTION_STATUS_EMOJI = {
  ok: '✅',
  warning: '⚠️',
  missing: '❌',
};

const formatRuntimeSeconds = (value) => {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds < 0) {
    return null;
  }
  if (seconds < 1) {
    return '<1s';
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds - minutes * 60);
  if (minutes && remainingSeconds) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  if (minutes) {
    return `${minutes}m`;
  }
  return `${remainingSeconds}s`;
};

const isTaskFreshInLocalTimezone = (task) => {
  if (!task || task.status === 'missing') {
    return false;
  }

  // BackendData freshness is date-only (no time component), so evaluate against
  // the user's local calendar day to avoid UTC rollover false-stale states.
  if (task.name === 'backend_data') {
    if (!task.max_created_date) {
      return false;
    }
    const maxCreatedDate = dayjs(task.max_created_date);
    if (!maxCreatedDate.isValid()) {
      return Boolean(task.fresh);
    }
    const localDayDiff = dayjs().startOf('day').diff(maxCreatedDate.startOf('day'), 'day');
    return localDayDiff <= 1;
  }

  if (task.finished_at) {
    const finishedAt = dayjs(task.finished_at);
    if (finishedAt.isValid()) {
      return dayjs().diff(finishedAt, 'hour', true) <= 24;
    }
  }

  return Boolean(task.fresh);
};

const buildTaskStatusText = (task) => {
  const parts = [];
  const statusLabel = (task?.status || 'missing').replace(/_/g, ' ');
  parts.push(statusLabel);

  const isFresh = isTaskFreshInLocalTimezone(task);
  if (task && !isFresh && task.status !== 'missing') {
    parts.push('stale');
  }

  const runtime = formatRuntimeSeconds(task?.runtime_seconds);
  if (runtime) {
    parts.push(runtime);
  }

  if (task?.finished_at) {
    parts.push(dayjs(task.finished_at).fromNow());
  } else if (task?.status !== 'missing') {
    parts.push('no recent run');
  }

  if (task?.max_created_date) {
    parts.push(`max date ${dayjs(task.max_created_date).format('YYYY-MM-DD')}`);
  }

  return parts.join(' • ');
};

const IngestionStatusIndicator = ({ ingestionHealth }) => {
  if (!ingestionHealth) {
    return null;
  }

  const tasks = Array.isArray(ingestionHealth.tasks) ? ingestionHealth.tasks : [];
  const isHealthyTask = (task) => {
    if (!task || task.status === 'missing') {
      return false;
    }
    if (task.name === 'backend_data') {
      return isTaskFreshInLocalTimezone(task);
    }
    return (
      String(task.status).toLowerCase() === 'succeeded' &&
      isTaskFreshInLocalTimezone(task)
    );
  };
  const statusKey = ingestionHealth.has_mapping
    ? tasks.length > 0 && tasks.every(isHealthyTask)
      ? 'ok'
      : 'warning'
    : 'missing';
  const symbol = INGESTION_STATUS_EMOJI[statusKey] || '';

  const tooltipContent = (
    <Box sx={{ maxWidth: 320 }}>
      <Typography variant="subtitle2" mb={0.5}>
        Data tasks
      </Typography>
      {!ingestionHealth.has_mapping ? (
        <Typography variant="body2" color="text.secondary">
          No email, API, SFTP, or Google Sheet mappings configured.
        </Typography>
      ) : tasks.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No recent task runs recorded.
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {tasks.map((task, index) => (
            <Box key={`${task?.name || 'task'}-${index}`}>
              <Typography variant="body2" fontWeight={600}>
                {task?.name || 'Task'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {buildTaskStatusText(task)}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );

  if (!symbol) {
    return null;
  }

  return (
    <Tooltip arrow placement="top" title={tooltipContent}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          fontSize: '1.05rem',
          ml: 0.5,
          cursor: 'default',
        }}
      >
        {symbol}
      </Box>
    </Tooltip>
  );
};

const parsePercentageValue = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const getPacingSeverity = (value) => {
  const metric = parsePercentageValue(value);
  if (metric === null) {
    return null;
  }
  if (metric < 70) return 'critical_under';
  if (metric < 90) return 'watch_under';
  if (metric <= 110) return 'healthy';
  return 'critical_over';
};

const pacingSeverityStyles = {
  critical_under: (theme) => ({
    color: theme.palette.error.dark,
    backgroundColor: safeAlpha(theme.palette.error.light || theme.palette.error.main, 0.35),
    borderColor: safeAlpha(theme.palette.error.main, 0.45),
  }),
  watch_under: (theme) => ({
    color: theme.palette.warning.dark || theme.palette.warning.main,
    backgroundColor: safeAlpha(theme.palette.warning.light || theme.palette.warning.main, 0.25),
    borderColor: safeAlpha(theme.palette.warning.main, 0.45),
  }),
  healthy: marketingSeverityStyles.low,
  watch_over: (theme) => ({
    color: theme.palette.warning.dark || theme.palette.warning.main,
    backgroundColor: safeAlpha(theme.palette.warning.light || theme.palette.warning.main, 0.25),
    borderColor: safeAlpha(theme.palette.warning.main, 0.45),
  }),
  critical_over: (theme) => ({
    color: theme.palette.error.dark,
    backgroundColor: safeAlpha(theme.palette.error.light || theme.palette.error.main, 0.35),
    borderColor: safeAlpha(theme.palette.error.main, 0.45),
  }),
};

const PacingPill = ({ value }) => {
  const severity = getPacingSeverity(value);
  const display = (
    <PercentageFormatter value={value} isRatio={false} />
  );

  if (!severity) {
    return display;
  }

  return (
    <Box
      component="span"
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: '0.85rem',
        px: 1,
        py: 0.25,
        borderRadius: theme.shape.borderRadius * 2,
        border: '1px solid',
        minWidth: theme.spacing(8),
        ...pacingSeverityStyles[severity](theme),
      })}
    >
      {display}
    </Box>
  );
};

const RemainingBudgetPercentagePill = ({ value }) => {
  const metric = parsePercentageValue(value);
  const display = (
    <PercentageFormatter value={value} isRatio={false} />
  );

  if (metric === null || metric >= 5) {
    return display;
  }

  return (
    <Box
      component="span"
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: '0.85rem',
        px: 1,
        py: 0.25,
        borderRadius: theme.shape.borderRadius * 2,
        border: '1px solid',
        minWidth: theme.spacing(8),
        color: theme.palette.error.dark,
        backgroundColor: safeAlpha(
          theme.palette.error.light || theme.palette.error.main,
          0.35
        ),
        borderColor: safeAlpha(theme.palette.error.main, 0.45),
      })}
    >
      {display}
    </Box>
  );
};

const OutOfAreaRatePill = ({ value }) => {
  const metric = parseRatioValue(value);
  const limit = 0.2;
  const display = <PercentageFormatter value={value} />;

  if (metric === null || metric <= limit) {
    return display;
  }

  return (
    <Box
      component="span"
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: '0.85rem',
        px: 1,
        py: 0.25,
        borderRadius: theme.shape.borderRadius * 2,
        border: '1px solid',
        minWidth: theme.spacing(6),
        color: theme.palette.error.dark,
        backgroundColor: safeAlpha(
          theme.palette.error.light || theme.palette.error.main,
          0.25
        ),
        borderColor: safeAlpha(theme.palette.error.main, 0.45),
      })}
    >
      {display}
    </Box>
  );
};

const AcceptedRatePill = ({ value }) => {
  const metric = parseRatioValue(value);
  const threshold = 0.8;
  const display = <PercentageFormatter value={value} />;

  if (metric === null || metric >= threshold) {
    return display;
  }

  return (
    <Box
      component="span"
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: '0.85rem',
        px: 1,
        py: 0.25,
        borderRadius: theme.shape.borderRadius * 2,
        border: '1px solid',
        minWidth: theme.spacing(6),
        color: theme.palette.error.dark,
        backgroundColor: safeAlpha(
          theme.palette.error.light || theme.palette.error.main,
          0.25
        ),
        borderColor: safeAlpha(theme.palette.error.main, 0.45),
      })}
    >
      {display}
    </Box>
  );
};

const HighlightIfZero = ({ value, children }) => {
  const numeric = toFiniteNumber(value);
  const isZero = numeric === 0;

  if (!isZero) {
    return children;
  }

  return (
    <Box
      component="span"
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: '0.85rem',
        px: 1,
        py: 0.25,
        borderRadius: theme.shape.borderRadius * 2,
        border: '1px solid',
        minWidth: theme.spacing(6),
        color: theme.palette.error.dark,
        backgroundColor: safeAlpha(
          theme.palette.error.light || theme.palette.error.main,
          0.25
        ),
        borderColor: safeAlpha(theme.palette.error.main, 0.45),
      })}
    >
      {children}
    </Box>
  );
};

const WholeDollarFormatter = (props) => (
  <DollarFormatter {...props} fractionDigits={0} />
);

const getDaysLeftInMonth = () => {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return lastDay - now.getDate();
};

const getExhaustionDate = (row) => {
  const remaining = Number(row?.budget_remaining);
  const yesterdaySpend = Number(row?.yesterday_spend);
  if (!Number.isFinite(remaining) || !Number.isFinite(yesterdaySpend) || yesterdaySpend <= 0 || remaining <= 0) {
    return null;
  }
  const daysUntilExhaustion = remaining / yesterdaySpend;
  const exhaustionDate = new Date();
  exhaustionDate.setDate(exhaustionDate.getDate() + Math.floor(daysUntilExhaustion));
  return exhaustionDate;
};

const formatExhaustionDate = (date) => {
  if (!date) return '-';
  return dayjs(date).format('MMM D');
};

const isExhaustionBeforeMonthEnd = (row) => {
  const date = getExhaustionDate(row);
  if (!date) return false;
  const now = new Date();
  const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return date <= lastDayOfMonth;
};

const ExhaustionDatePill = ({ row }) => {
  const date = getExhaustionDate(row);
  const label = formatExhaustionDate(date);
  const atRisk = isExhaustionBeforeMonthEnd(row);

  if (!date) {
    return <Typography variant="body2" color="text.secondary">-</Typography>;
  }

  return (
    <Box
      component="span"
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: '0.85rem',
        px: 1,
        py: 0.25,
        borderRadius: theme.shape.borderRadius * 2,
        border: '1px solid',
        minWidth: theme.spacing(8),
        ...(atRisk
          ? {
              color: theme.palette.error.dark,
              backgroundColor: safeAlpha(theme.palette.error.light || theme.palette.error.main, 0.35),
              borderColor: safeAlpha(theme.palette.error.main, 0.45),
            }
          : {
              color: theme.palette.success.dark,
              backgroundColor: safeAlpha(theme.palette.success.light || theme.palette.success.main, 0.2),
              borderColor: safeAlpha(theme.palette.success.main, 0.35),
            }),
      })}
    >
      {label}
    </Box>
  );
};

const getRemainingBudgetPercentage = (remaining, budget) => {
  const remainingValue = Number(remaining);
  const budgetValue = Number(budget);
  if (
    !Number.isFinite(remainingValue) ||
    !Number.isFinite(budgetValue) ||
    budgetValue === 0
  ) {
    return null;
  }

  const percent = (remainingValue / budgetValue) * 100;
  return Number.isFinite(percent) ? percent : null;
};

const getFacebookBudgetPacePercentage = (row) => {
  const dailyBudget = Number(row?.facebook_daily_budget_total ?? row?.facebook_budget_amount);
  const remainingBudgetByDay = Number(
    row?.calendar_budget_remaining_per_day ?? row?.remaining_per_day
  );
  if (
    !Number.isFinite(dailyBudget) ||
    dailyBudget <= 0 ||
    !Number.isFinite(remainingBudgetByDay) ||
    remainingBudgetByDay <= 0
  ) {
    return null;
  }

  return (dailyBudget / remainingBudgetByDay) * 100;
};

const deriveRates = (row) => {
  const acceptedRateFromSums = safeRatio(row?.accepted_sum, row?.accepted_sum, row?.rejected_sum);
  const outOfAreaRateFromSums = safeRatio(row?.out_of_areas_count_sum, row?.total_leads_sum);
  const selfBookRateFromSums = safeRatio(row?.self_books_sum, row?.total_leads_sum);

  return {
    acceptedRate: acceptedRateFromSums ?? row?.accepted_avg,
    outOfAreaRate: outOfAreaRateFromSums ?? row?.out_of_area_avg,
    selfBookRate: selfBookRateFromSums ?? row?.self_book_rate_avg,
  };
};

const sumRowValues = (rows, selector) =>
  rows.reduce((total, row) => {
    const value = toFiniteNumber(selector(row));
    return total + (value ?? 0);
  }, 0);

const buildClientTotalsRow = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return null;
  }

  const totalSpend = sumRowValues(rows, (row) => row?.total_spend_sum);
  const totalSpendPlusFees = sumRowValues(rows, (row) => row?.spendPlusFees);
  const totalProjectedSpend = sumRowValues(rows, (row) => row?.projected_spend);
  const totalBudget = sumRowValues(rows, (row) => row?.budget_amount);
  const totalBudgetRemaining = sumRowValues(rows, (row) => row?.budget_remaining);
  const totalRemainingPerDay = sumRowValues(
    rows,
    (row) => row?.calendar_budget_remaining_per_day
  );
  const totalYesterdaySpend = sumRowValues(rows, (row) => row?.yesterday_spend);
  const totalFacebookBudget = sumRowValues(
    rows,
    (row) => row?.facebook_daily_budget_total ?? row?.facebook_budget_amount
  );
  const totalLeads = sumRowValues(rows, (row) => row?.total_leads_sum);
  const totalAccepted = sumRowValues(rows, (row) => row?.accepted_sum);
  const totalRejected = sumRowValues(rows, (row) => row?.rejected_sum);
  const totalOutOfArea = sumRowValues(rows, (row) => row?.out_of_areas_count_sum);
  const totalSelfBooks = sumRowValues(rows, (row) => row?.self_books_sum);
  const totalBooked = sumRowValues(rows, (row) => row?.booked_sum);
  const totalScheduled = sumRowValues(rows, (row) => row?.scheduled_sum);
  const totalIssues = sumRowValues(rows, (row) => row?.issues_sum);
  const totalDemos = sumRowValues(rows, (row) => row?.demos_sum);
  const totalGrossSales = sumRowValues(rows, (row) => row?.gross_sales_sum);
  const totalGrossAmount = sumRowValues(rows, (row) => row?.gross_amount_sum);
  const totalNetSales = sumRowValues(rows, (row) => row?.net_sales_sum);
  const totalNetAmount = sumRowValues(rows, (row) => row?.net_amount_sum);

  const costPerLead =
    totalLeads > 0 ? totalSpend / totalLeads : null;
  const costPerSelfBook =
    totalSelfBooks > 0 ? totalSpend / totalSelfBooks : null;
  const costOfMarketing =
    totalNetAmount > 0 ? totalSpendPlusFees / totalNetAmount : null;
  const pacingPercentage =
    totalBudget > 0 ? (totalProjectedSpend / totalBudget) * 100 : null;

  return {
    uuid: 'daily-pulse-total-row',
    client__name: 'Total',
    total_spend_sum: totalSpend,
    spendPlusFees: totalSpendPlusFees,
    costOfMarketing,
    projected_spend: totalProjectedSpend,
    pacing_percentage: pacingPercentage,
    budget_amount: totalBudget,
    budget_remaining: totalBudgetRemaining,
    calendar_budget_remaining_per_day: totalRemainingPerDay,
    yesterday_spend: totalYesterdaySpend,
    facebook_daily_budget_total: totalFacebookBudget,
    total_leads_sum: totalLeads,
    cost_per_lead_avg: costPerLead,
    accepted_sum: totalAccepted,
    rejected_sum: totalRejected,
    out_of_areas_count_sum: totalOutOfArea,
    self_books_sum: totalSelfBooks,
    cost_per_self_book_avg: costPerSelfBook,
    booked_sum: totalBooked,
    scheduled_sum: totalScheduled,
    issues_sum: totalIssues,
    demos_sum: totalDemos,
    gross_sales_sum: totalGrossSales,
    gross_amount_sum: totalGrossAmount,
    net_sales_sum: totalNetSales,
    net_amount_sum: totalNetAmount,
  };
};

const GOOGLE_ADS_STATIC_PARAMS =
  '&euid=391788443&__u=1069503107&uscid=7044144174&__c=3239473726&authuser=0&__e=2441890378';

const buildGoogleAdsLink = (customerId) =>
  `https://ads.google.com/aw/overview?ocid=${customerId}&ascid=${customerId}${GOOGLE_ADS_STATIC_PARAMS}`;

const toLookupKey = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  return String(value);
};

const buildClientAdAccountLookup = (clients) => {
  if (!Array.isArray(clients)) {
    return {};
  }

  return clients.reduce((acc, client = {}) => {
    const key = toLookupKey(client?.id);
    if (!key) {
      return acc;
    }

    const facebookLinks = (Array.isArray(client.facebook_accounts) ? client.facebook_accounts : [])
      .filter((account) => account?.account_id)
      .map((account) => ({
        key: `fb-${account.account_id}`,
        href: `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${account.account_id}`,
        label: `Facebook: ${account.account_name || account.account_id}`,
        title: account.account_name || account.account_id,
        platform: 'facebook',
      }));

    const tiktokLinks = (Array.isArray(client.tiktok_advertisers) ? client.tiktok_advertisers : [])
      .filter((advertiser) => advertiser?.advertiser_id)
      .map((advertiser) => ({
        key: `tt-${advertiser.advertiser_id}`,
        href: `https://ads.tiktok.com/i18n/manage/campaign?aadvid=${advertiser.advertiser_id}`,
        label: `TikTok: ${advertiser.advertiser_name || advertiser.advertiser_id}`,
        title: advertiser.advertiser_name || advertiser.advertiser_id,
        platform: 'tiktok',
      }));

    const googleLinks = (Array.isArray(client.google_customers) ? client.google_customers : [])
      .filter((customer) => customer?.customer_id)
      .map((customer) => ({
        key: `gg-${customer.customer_id}`,
        href: buildGoogleAdsLink(customer.customer_id),
        label: `Google: ${customer.customer_name || customer.customer_id}`,
        title: customer.customer_name || customer.customer_id,
        platform: 'google',
      }));

    const links = [...facebookLinks, ...tiktokLinks, ...googleLinks];
    if (links.length > 0) {
      acc[key] = links;
    }
    return acc;
  }, {});
};

const FacebookBadgeIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 16 16" role="img" aria-label="Facebook" {...props}>
    <rect width="16" height="16" rx="4" fill="#1877F2" />
    <path
      d="M9.333 6H10.667V4.133H9.4C7.933 4.133 6.933 5.133 6.933 6.6V7.8H5.333V9.933H6.933V16H9.4V9.933H11L11.333 7.8H9.4V6.733C9.4 6.293 9.667 6 9.333 6Z"
      fill="#fff"
    />
  </svg>
);

const GoogleBadgeIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 18 18" role="img" aria-label="Google" {...props}>
    <rect x="1" y="1" width="16" height="16" rx="8" fill="#fff" stroke="#E0E0E0" strokeWidth="0.5" />
    <path
      fill="#4285F4"
      d="M16.318 8.205c0-.546-.049-1.07-.141-1.574H9v2.977h4.082c-.175.95-.702 1.758-1.494 2.295v1.908h2.414c1.414-1.302 2.316-3.219 2.316-5.606z"
    />
    <path
      fill="#34A853"
      d="M9 16.5c1.98 0 3.64-.655 4.853-1.776l-2.414-1.908c-.67.45-1.53.717-2.439.717-1.874 0-3.463-1.266-4.032-2.966H2.448v1.996A7.495 7.495 0 009 16.5z"
    />
    <path
      fill="#FBBC05"
      d="M4.968 10.567A4.495 4.495 0 014.73 9c0-.548.094-1.084.238-1.567V5.437H2.448A7.5 7.5 0 001.5 9c0 1.21.289 2.352.948 3.563l2.52-1.996z"
    />
    <path
      fill="#EA4335"
      d="M9 4.5c1.08 0 2.045.372 2.804 1.098l2.099-2.099C12.64 2.387 10.98 1.5 9 1.5a7.495 7.495 0 00-6.552 3.937l2.52 1.996C5.537 5.766 7.126 4.5 9 4.5z"
    />
  </svg>
);

const TikTokBadgeIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 16 16" role="img" aria-label="TikTok" {...props}>
    <rect width="16" height="16" rx="4" fill="#010101" />
    <path
      d="M7.2 13.6c-1.43 0-2.6-1.17-2.6-2.6 0-1.177.784-2.167 1.86-2.483v1.667a.95.95 0 00-.56.876c0 .526.43.956.96.956a1.01 1.01 0 00.7-.296V4.8h2.3c.026 1.232 1.018 2.224 2.25 2.25V8.8a3.47 3.47 0 01-2.25-.741v3.115c0 1.745-1.415 3.159-3.16 3.159Z"
      fill="#25F4EE"
    />
    <path
      d="M6.4 12.667c-1.01 0-1.833-.822-1.833-1.833 0-.42.146-.806.39-1.11V6.133H3.6v2.266c-.84.504-1.4 1.414-1.4 2.435 0 1.58 1.28 2.86 2.86 2.86 1.014 0 1.9-.517 2.433-1.302v-1.767c-.223.17-.5.272-.813.272-.69 0-1.25-.56-1.25-1.25 0-.07.006-.14.017-.207"
      fill="#FE2C55"
    />
    <path
      d="M10.6 5.2V4H8.3v7.633a1.02 1.02 0 01-.76.323c-.54 0-.98-.44-.98-.98 0-.54.44-.98.98-.98.34 0 .64.18.807.443V7.214c.62.43 1.377.686 2.173.686.2 0 .39-.017.58-.045V6.8a3.51 3.51 0 01-1.5-.934Z"
      fill="#fff"
    />
  </svg>
);

const PLATFORM_ICON_COMPONENTS = {
  facebook: FacebookBadgeIcon,
  google: GoogleBadgeIcon,
  tiktok: TikTokBadgeIcon,
};

const COLUMNS = [
  { id: 'client', label: 'Client', sortField: 'client__name', locked: true },
  { id: 'parent_company', label: 'Parent Co.', defaultVisible: false },
  { id: 'ad_accounts', label: 'Ad Accounts' },
  { id: 'cost_of_marketing', label: 'Cost of Mktg' },
  { id: 'total_spend', label: 'Spend', sortField: 'total_spend_sum' },
  { id: 'spend_plus_fees', label: 'Spend + Fees' },
  { id: 'projected_spend', label: 'Proj. Spend' },
  { id: 'pacing', label: 'Pacing %' },
  { id: 'budget', label: 'Budget' },
  { id: 'budget_remaining', label: 'Remaining' },
  { id: 'remaining_per_day', label: 'Rem. /Day' },
  { id: 'yesterday_spend', label: 'Yest. Spend' },
  { id: 'exhaustion_date', label: 'Budget Runs Out' },
  { id: 'fb_budget', label: 'FB Budget' },
  { id: 'fb_pace', label: 'FB Pace %' },
  { id: 'leads', label: 'Leads', sortField: 'total_leads_sum' },
  { id: 'cpl', label: 'CP Lead', sortField: 'cost_per_lead_avg' },
  { id: 'accepted_rate', label: 'Accept Rate', sortField: 'accepted_avg' },
  { id: 'out_rate', label: 'Out Rate', sortField: 'out_of_area_avg' },
  { id: 'self_books', label: 'Self Books', sortField: 'self_books_sum' },
  { id: 'cp_self_book', label: 'CP Self Book', sortField: 'cost_per_self_book_avg' },
  { id: 'self_book_rate', label: 'SB Rate', sortField: 'self_book_rate_avg' },
  { id: 'booked', label: 'Booked', sortField: 'booked_sum' },
  { id: 'scheduled', label: 'Scheduled', sortField: 'scheduled_sum' },
  { id: 'issues', label: 'Issues', sortField: 'issues_sum' },
  { id: 'demos', label: 'Demos', sortField: 'demos_sum' },
  { id: 'gross_sales', label: 'Gross Sales', sortField: 'gross_sales_sum' },
  { id: 'gross_amount', label: 'Gross Amt', sortField: 'gross_amount_sum' },
  { id: 'net_sales', label: 'Net Sales', sortField: 'net_sales_sum' },
  { id: 'net_amount', label: 'Net Amt', sortField: 'net_amount_sum' },
];

const DEFAULT_VISIBLE_COLS = new Set(COLUMNS.filter((c) => c.defaultVisible !== false).map((c) => c.id));
const COLUMN_VISIBILITY_STORAGE_KEY = 'daily_pulse_visible_columns';

const getInitialVisibleColumns = () => {
  if (typeof window === 'undefined') return DEFAULT_VISIBLE_COLS;
  try {
    const raw = window.localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
    if (!raw) return DEFAULT_VISIBLE_COLS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_VISIBLE_COLS;
    const validIds = new Set(COLUMNS.map((c) => c.id));
    const restored = parsed.filter((id) => validIds.has(id));
    // Always include locked columns
    COLUMNS.forEach((c) => { if (c.locked && !restored.includes(c.id)) restored.push(c.id); });
    return restored.length > 0 ? new Set(restored) : DEFAULT_VISIBLE_COLS;
  } catch {
    return DEFAULT_VISIBLE_COLS;
  }
};

const persistVisibleColumns = (colSet) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify([...colSet]));
  } catch { /* ignore */ }
};

const DATE_PRESETS = [
  { label: 'This Month', getRange: () => { const now = new Date(); return [new Date(now.getFullYear(), now.getMonth(), 1), new Date(now.getFullYear(), now.getMonth() + 1, 0)]; } },
  { label: 'Last 7d', getRange: () => { const end = new Date(); end.setDate(end.getDate() - 1); const start = new Date(end); start.setDate(start.getDate() - 6); return [start, end]; } },
  { label: 'Last 30d', getRange: () => { const end = new Date(); end.setDate(end.getDate() - 1); const start = new Date(end); start.setDate(start.getDate() - 29); return [start, end]; } },
  { label: 'Last Month', getRange: () => { const now = new Date(); return [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 0)]; } },
];

const SKELETON_ROWS = 8;

export default function DailyPulse() {
  const dailyPulse = useDailyPulse() ?? {};
  const {
    data,
    tree = [],
    clientSummary,
    startDate,
    endDate,
    setDateRange: setDateRangeFromContext,
    isLoading = false,
    orderBy = 'total_spend_sum',
    orderDirection = 'desc',
    setSort: setSortFromContext,
    refreshData: refreshDataFromContext,
    lastRefreshedAt = null,
    loadData,
    clientStatusFilter = DEFAULT_CLIENT_STATUS_FILTER,
    setClientStatusFilter: setClientStatusFilterFromContext,
    clientTierFilter = [],
    setClientTierFilter: setClientTierFilterFromContext,
    clientPage = 1,
    setClientPage: setClientPageFromContext,
    clientPageSize = 50,
    setClientPageSize: setClientPageSizeFromContext,
    clientTotalCount = 0,
  } = dailyPulse;

  const rows = React.useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const [clientAccountLookup, setClientAccountLookup] = React.useState({});
  const [clientSlugLookup, setClientSlugLookup] = React.useState({});
  const [parentCompanyLookup, setParentCompanyLookup] = React.useState({});

  // Column picker state
  const [visibleCols, setVisibleCols] = React.useState(getInitialVisibleColumns);
  const [colPickerAnchor, setColPickerAnchor] = React.useState(null);
  const isCol = React.useCallback((id) => visibleCols.has(id), [visibleCols]);
  const toggleColumn = React.useCallback((id) => {
    setVisibleCols((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      persistVisibleColumns(next);
      return next;
    });
  }, []);

  // Search state
  const [searchQuery, setSearchQuery] = React.useState('');
  const searchRef = React.useRef(null);

  // Expandable rows state
  const [expandedClients, setExpandedClients] = React.useState(new Set());

  React.useEffect(() => {
    let isMounted = true;

    api
      .request({
        url: 'dashboard/clients/',
        method: 'GET',
        params: {
          fields:
            'id,slug,parent_company,parent_company_name,facebook_ad_accounts,tiktok_ad_accounts,google_ad_customers',
          page_size: 500,
        },
      })
      .then((response) => {
        if (!isMounted) {
          return;
        }
        const payload = response?.data;
        const clients = Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload)
            ? payload
            : [];
        setClientAccountLookup(buildClientAdAccountLookup(clients));
        const slugMap = clients.reduce((acc, client) => {
          const key = toLookupKey(client?.id);
          if (key && client?.slug) {
            acc[key] = client.slug;
          }
          return acc;
        }, {});
        setClientSlugLookup(slugMap);
        const parentMap = clients.reduce((acc, client) => {
          const key = toLookupKey(client?.id);
          if (!key) {
            return acc;
          }
          acc[key] = client?.parent_company_name || null;
          return acc;
        }, {});
        setParentCompanyLookup(parentMap);
      })
      .catch((error) => {
        if (isMounted) {
          console.error('Failed to fetch client ad accounts for Daily Pulse', error);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const resolvedStatusFilter = Array.isArray(clientStatusFilter)
    ? clientStatusFilter
    : DEFAULT_CLIENT_STATUS_FILTER;
  const resolvedTierFilter = Array.isArray(clientTierFilter) ? clientTierFilter : [];
  const totalPages = Math.max(1, Math.ceil((clientTotalCount || 0) / Math.max(clientPageSize, 1)));

  const updateStatusFilter = React.useCallback(
    (next) => {
      if (typeof setClientStatusFilterFromContext === 'function') {
        setClientStatusFilterFromContext(next);
      }
    },
    [setClientStatusFilterFromContext],
  );

  const updateTierFilter = React.useCallback(
    (next) => {
      if (typeof setClientTierFilterFromContext === 'function') {
        setClientTierFilterFromContext(next);
      }
    },
    [setClientTierFilterFromContext],
  );

  const normalizeMultiValue = (value) => {
    if (typeof value === 'string') {
      return value.split(',').filter(Boolean);
    }
    return Array.isArray(value) ? value : [];
  };

  const handleStatusFilterChange = (event) => {
    updateStatusFilter(normalizeMultiValue(event.target.value));
  };

  const handleTierFilterChange = (event) => {
    updateTierFilter(normalizeMultiValue(event.target.value));
  };

  const tierOptions = React.useMemo(() => {
    const byId = new Map();
    rows.forEach((row = {}) => {
      const tierId = row?.client__tier;
      if (tierId === null || tierId === undefined) {
        return;
      }
      const key = String(tierId);
      if (!byId.has(key)) {
        byId.set(key, {
          value: key,
          label: row?.['client__tier__name'] || `Tier ${key}`,
        });
      }
    });
    return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const tierLabelLookup = React.useMemo(
    () =>
      tierOptions.reduce((acc, option) => {
        acc[option.value] = option.label;
        return acc;
      }, {}),
    [tierOptions],
  );

  const displayRows = React.useMemo(() => {
    if (
      !Array.isArray(rows) ||
      rows.length === 0 ||
      orderBy !== 'total_spend_sum' ||
      orderDirection !== 'desc'
    ) {
      return rows;
    }

    const getPriority = (status) => {
      const rank = PRIORITY_STATUS_ORDER[status];
      return typeof rank === 'number' ? rank : PRIORITY_CLIENT_STATUSES.length;
    };

    const getSpend = (row) => {
      const value = Number(row?.total_spend_sum ?? row?.total_spend);
      return Number.isFinite(value) ? value : 0;
    };

    return [...rows].sort((a, b) => {
      const priorityDelta = getPriority(a?.client__status) - getPriority(b?.client__status);
      if (priorityDelta !== 0) {
        return priorityDelta;
      }

      const spendDelta = getSpend(b) - getSpend(a);
      if (spendDelta !== 0) {
        return spendDelta;
      }

      return (a?.client__name || '').localeCompare(b?.client__name || '');
    });
  }, [rows, orderBy, orderDirection]);

  const renderStatusValue = React.useCallback((selected) => {
    if (
      !Array.isArray(selected) ||
      selected.length === 0 ||
      selected.length === CLIENT_STATUS_OPTIONS.length
    ) {
      return 'All Statuses';
    }
    return selected.map((value) => STATUS_LABEL_LOOKUP[value] || value).join(', ');
  }, []);

  const renderTierValue = React.useCallback(
    (selected) => {
      if (
        !Array.isArray(selected) ||
        selected.length === 0 ||
        (tierOptions.length > 0 && selected.length === tierOptions.length)
      ) {
        return 'All Tiers';
      }
      return selected.map((value) => tierLabelLookup[value] || value).join(', ');
    },
    [tierLabelLookup, tierOptions.length],
  );

  const setDateRange = React.useCallback((...args) => {
    if (typeof setDateRangeFromContext === 'function') {
      setDateRangeFromContext(...args);
    }
  }, [setDateRangeFromContext]);

  const setSort = React.useCallback(
    (field) => {
      if (typeof setSortFromContext === 'function') {
        setSortFromContext(field);
      }
    },
    [setSortFromContext]
  );

  // Expand/collapse client rows
  const toggleClientExpanded = React.useCallback((row) => {
    const clientId = row?.client_id;
    if (!clientId) return;
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) { next.delete(clientId); } else {
        next.add(clientId);
        // Load drill-down data if not already loaded
        const clientNode = tree.find((c) => c.client_id === clientId);
        if (!clientNode?.channels?.length && typeof loadData === 'function') {
          loadData(row);
        }
      }
      return next;
    });
  }, [tree, loadData]);

  // Date presets
  const handleDatePreset = React.useCallback((preset) => {
    const [start, end] = preset.getRange();
    if (typeof setDateRangeFromContext === 'function') {
      setDateRangeFromContext(start, end);
    }
  }, [setDateRangeFromContext]);

  // Keyboard shortcuts
  React.useEffect(() => {
    const handler = (e) => {
      // Don't capture when typing in an input
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        if (typeof refreshDataFromContext === 'function') refreshDataFromContext();
      }
      if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [refreshDataFromContext]);

  const handleRefresh = React.useCallback(() => {
    if (typeof refreshDataFromContext === 'function') {
      refreshDataFromContext();
    }
  }, [refreshDataFromContext]);

  const handlePageChange = React.useCallback(
    (_event, nextPageZeroBased) => {
      if (typeof setClientPageFromContext === 'function') {
        setClientPageFromContext(nextPageZeroBased + 1);
      }
    },
    [setClientPageFromContext]
  );

  const handlePageSizeChange = React.useCallback(
    (event) => {
      const next = Number(event.target.value);
      if (typeof setClientPageSizeFromContext === 'function' && Number.isFinite(next) && next > 0) {
        setClientPageSizeFromContext(next);
      }
    },
    [setClientPageSizeFromContext]
  );

  const lastRefreshedLabel = React.useMemo(() => {
    if (!lastRefreshedAt) {
      return 'Not refreshed yet';
    }
    const refreshed = new Date(lastRefreshedAt);
    if (Number.isNaN(refreshed.getTime())) {
      return 'Not refreshed yet';
    }
    return refreshed.toLocaleString();
  }, [lastRefreshedAt]);

  const handleSort = (field) => setSort(field);

  const handleDateRangeChange = (e) => {
    const { startDate, endDate } = e.selection;
    setDateRange(startDate, endDate);
  };

  const stickyHeaderBaseSx = {
    position: 'sticky',
    top: 0,
    backgroundColor: (theme) => theme.palette.background.paper,
    zIndex: 3
  };

  const stickyClientHeaderSx = {
    ...stickyHeaderBaseSx,
    left: 0,
    minWidth: 200,
    whiteSpace: 'normal',
    lineHeight: 1.2
  };

  const stickyClientCellSx = {
    position: 'sticky',
    left: 0,
    backgroundColor: (theme) => theme.palette.background.paper,
    zIndex: 2,
    minWidth: 200,
    maxWidth: 300,
    whiteSpace: 'normal',
    lineHeight: 1.2,
  };

  const tableContainerSx = {
    maxHeight: '75vh',
    minHeight: 520,
    borderRadius: 2,
    border: (theme) => `1px solid ${theme.palette.divider}`,
    boxShadow: (theme) => theme.shadows[1],
    backgroundColor: (theme) => theme.palette.background.paper,
  };

  const mainTableSx = {
    minWidth: 1600,
    '& .MuiTableCell-root': {
      borderBottomColor: (theme) => safeAlpha(theme?.palette?.divider, 0.7),
      paddingTop: (theme) => theme.spacing(1),
      paddingBottom: (theme) => theme.spacing(1),
    },
    '& thead .MuiTableCell-root': {
      whiteSpace: 'normal',
      lineHeight: 1.2,
    },
    '& tbody .MuiTableCell-root': {
      whiteSpace: 'nowrap',
    },
    '& tbody tr': {
      backgroundColor: (theme) => theme.palette.background.paper,
    },
    '& tbody tr:hover': {
      backgroundColor: (theme) => getHoverBackground(theme, 0.06),
    },
  };

  const clientNameLinkSx = {
    textDecoration: 'none',
    fontWeight: 600,
    display: 'block',
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.2,
    '&:hover': {
      textDecoration: 'underline',
    },
  };

  const clientAccountLinkSx = {
    color: 'text.secondary',
    textDecoration: 'none',
    fontSize: '0.7rem',
    lineHeight: 1.1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: '50%',
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: (theme) => theme.palette.background.default,
    transition: 'all 0.15s ease',
    '&:hover': {
      textDecoration: 'none',
      transform: 'translateY(-1px)',
      boxShadow: (theme) => theme.shadows[1],
    },
  };

  const clientAccountCellSx = {
    minWidth: 120,
    maxWidth: 160,
    whiteSpace: 'normal',
    lineHeight: 1.2,
    wordBreak: 'break-word',
  };

  const getClientNameColor = (status, theme) => {
    if (PRIORITY_CLIENT_STATUS_SET.has(status)) {
      return theme.palette.mode === 'dark' ? PURPLE_STATUS_COLOR_DARK : PURPLE_STATUS_COLOR;
    }
    return theme.palette.primary?.main || PURPLE_STATUS_COLOR;
  };

  const getClientNameSx = (status) => (theme) => ({
    ...clientNameLinkSx,
    color: getClientNameColor(status, theme),
  });

  const LaunchDateBadge = ({ status, label }) => {
    if (!label) {
      return null;
    }
    return (
      <Typography
        component="span"
        sx={(theme) => {
          const color = getClientNameColor(status, theme);
          return {
            fontSize: '0.72rem',
            fontWeight: 600,
            color,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
          };
        }}
      >
        {label}
      </Typography>
    );
  };

  const buildClientDashboardPath = (clientId) => {
    if (!clientId) {
      return null;
    }
    const key = toLookupKey(clientId);
    const slug = key ? clientSlugLookup[key] : null;
    const identifier = slug || clientId;
    return `/clients/${identifier}/dashboard`;
  };

  const normalizeSlug = (value = '') =>
    value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const getParentNameFromRow = React.useCallback(
    (row = {}) =>
      parentCompanyLookup[toLookupKey(row.client_id)] ||
      row.parent_company_name ||
      row.client__parent_company_name ||
      row.client__parent_company__name ||
      null,
    [parentCompanyLookup]
  );

  const parentDashboardOptions = React.useMemo(() => {
    const options = [];
    const seen = new Set();

    for (const row of displayRows || rows || []) {
      if (!row?.client_id || !row?.client__name || !shouldDisplayClientRow(row)) {
        continue;
      }
      const parentName = getParentNameFromRow(row);
      const path = parentName ? `/clients/${normalizeSlug(parentName)}/dashboard` : null;
      if (!path || !parentName) {
        continue;
      }
      if (seen.has(path)) {
        continue;
      }
      seen.add(path);
      options.push({ label: parentName, value: path });
    }

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [displayRows, rows, getParentNameFromRow]);

  const visibleClientRows = React.useMemo(
    () => {
      const base = (displayRows || rows || []).filter(
        (row) => row?.client__name && shouldDisplayClientRow(row)
      );
      if (!searchQuery.trim()) return base;
      const q = searchQuery.trim().toLowerCase();
      return base.filter((row) => {
        const name = (row.client__name || '').toLowerCase();
        const parent = (getParentNameFromRow(row) || '').toLowerCase();
        return name.includes(q) || parent.includes(q);
      });
    },
    [displayRows, rows, searchQuery, getParentNameFromRow]
  );

  const getClientChildren = React.useCallback(
    (clientId) => {
      const node = tree.find((c) => c.client_id === clientId);
      if (!node) return { channels: [], verticals: [] };
      return { channels: node.channels || [], verticals: node.verticals || [] };
    },
    [tree]
  );

  const totalsRow = React.useMemo(
    () => clientSummary || buildClientTotalsRow(visibleClientRows),
    [clientSummary, visibleClientRows]
  );

  const renderClientAccountLinks = (clientId) => {
    const lookupKey = toLookupKey(clientId);
    if (!lookupKey) {
      return null;
    }
    const links = clientAccountLookup[lookupKey];
    if (!Array.isArray(links) || links.length === 0) {
      return null;
    }

    return (
      <Stack
        direction="row"
        spacing={0.25}
        flexWrap="nowrap"
        sx={{
          overflow: 'hidden',
          '& > *': { flexShrink: 0 },
        }}
      >
        {links.map((link) => {
          const IconComponent = PLATFORM_ICON_COMPONENTS[link.platform] || LinkSimple;
          return (
            <Tooltip key={link.key} title={link.label || link.title || ''} arrow>
              <Typography
                component="a"
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={clientAccountLinkSx}
              >
                <IconComponent size={14} />
              </Typography>
            </Tooltip>
          );
        })}
      </Stack>
    );
  };

  const renderSortableHeader = (label, field, cellProps = {}) => (
    <TableCell sortDirection={orderBy === field ? orderDirection : false} {...cellProps}>
      <TableSortLabel
        active={orderBy === field}
        direction={orderBy === field ? orderDirection : 'asc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const compactHeaderSx = {
    maxWidth: 120,
    whiteSpace: 'normal',
    lineHeight: 1.2,
  };

  const extraCompactHeaderSx = {
    ...compactHeaderSx,
    maxWidth: 100,
  };

  const renderCostOfMarketingCell = (row, goalOverride = null) => {
    const goal = goalOverride ?? row.costOfMarketingGoal;
    const hasHistory =
      row.costOfMarketingLast30 !== undefined || row.costOfMarketingLast60 !== undefined;

    const tooltipContent = (
      <Stack spacing={0.6}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            Last 30 days
          </Typography>
          <Box mt={0.25}>
            <CostOfMarketingPill value={row.costOfMarketingLast30} goal={goal} />
          </Box>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            Last 60 days
          </Typography>
          <Box mt={0.25}>
            <CostOfMarketingPill value={row.costOfMarketingLast60} goal={goal} />
          </Box>
        </Box>
      </Stack>
    );

    const pill = <CostOfMarketingPill value={row.costOfMarketing} goal={goal} />;
    const pillWithTooltip = hasHistory ? (
      <Tooltip arrow placement="top" title={tooltipContent}>
        <Box component="span" sx={{ display: 'inline-flex' }}>
          {pill}
        </Box>
      </Tooltip>
    ) : (
      pill
    );

    return pillWithTooltip;
  };

  const renderDataCells = (row, opts = {}) => {
    const { acceptedRate, outOfAreaRate, selfBookRate } = deriveRates(row);
    const facebookBudgetPace = getFacebookBudgetPacePercentage(row);
    const remainingPct = getRemainingBudgetPercentage(row.budget_remaining, row.budget_amount);
    const isTotal = opts.isTotal;
    const indent = opts.indent || 0;
    const rowLabel = opts.label;

    return (
      <React.Fragment>
        {isCol('client') && (
          <TableCell sx={{ ...stickyClientCellSx, ...(indent ? { pl: 2 + indent * 2 } : {}) }}>
            {isTotal ? (
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{row.client__name || 'Total'}</Typography>
            ) : rowLabel ? (
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>{rowLabel}</Typography>
            ) : (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                {row.client_id ? (
                  <IconButton size="small" sx={{ p: 0, flexShrink: 0 }} onClick={(e) => { e.stopPropagation(); toggleClientExpanded(row); }}>
                    {expandedClients.has(row.client_id) ? <KeyboardArrowDown sx={{ fontSize: 18 }} /> : <KeyboardArrowRight sx={{ fontSize: 18 }} />}
                  </IconButton>
                ) : null}
                {row.client_id ? (
                  <Typography
                    component={RouterLink}
                    href={buildClientDashboardPath(row.client_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="subtitle2"
                    onClick={(e) => e.stopPropagation()}
                    sx={getClientNameSx(row.client__status)}
                  >
                    {row.client__name}
                  </Typography>
                ) : (
                  <Typography variant="subtitle2" sx={clientNameLinkSx}>
                    {row.client__name}
                  </Typography>
                )}
                {(() => {
                  const launchDateLabel = getClientLaunchDateLabel(row.client__status, row.client__launch_date);
                  return launchDateLabel ? <LaunchDateBadge status={row.client__status} label={launchDateLabel} /> : null;
                })()}
                <IngestionStatusIndicator ingestionHealth={row.ingestion_health} />
              </Stack>
            )}
          </TableCell>
        )}
        {isCol('parent_company') && (
          <TableCell sx={compactHeaderSx}>
            <Typography variant="caption" color="text.secondary">{isTotal ? '-' : getParentNameFromRow(row) || '-'}</Typography>
          </TableCell>
        )}
        {isCol('ad_accounts') && (
          <TableCell sx={clientAccountCellSx}>
            {isTotal ? '-' : renderClientAccountLinks(row.client_id)}
          </TableCell>
        )}
        {isCol('cost_of_marketing') && <TableCell>{renderCostOfMarketingCell(row)}</TableCell>}
        {isCol('total_spend') && <TableCell><WholeDollarFormatter value={row.total_spend_sum} /></TableCell>}
        {isCol('spend_plus_fees') && <TableCell><WholeDollarFormatter value={row.spendPlusFees} /></TableCell>}
        {isCol('projected_spend') && <TableCell><WholeDollarFormatter value={row.projected_spend} /></TableCell>}
        {isCol('pacing') && <TableCell><PacingPill value={row.pacing_percentage} /></TableCell>}
        {isCol('budget') && (
          <TableCell>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ display: 'inline-flex' }}>
              <WholeDollarFormatter value={row.budget_amount} />
              {row.budget_workflow_status === 'draft' ? (
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    px: 0.5,
                    py: 0.1,
                    borderRadius: 1,
                    border: '1px solid',
                    color: 'info.dark',
                    bgcolor: 'rgba(59,130,246,0.12)',
                    borderColor: 'rgba(59,130,246,0.4)',
                    lineHeight: 1,
                  }}
                >
                  Draft
                </Box>
              ) : null}
            </Stack>
          </TableCell>
        )}
        {isCol('budget_remaining') && (
          <TableCell>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <HighlightIfZero value={row.budget_remaining}>
                <WholeDollarFormatter value={row.budget_remaining} />
              </HighlightIfZero>
              {remainingPct !== null && (
                <Typography component="span" variant="caption" color="text.secondary">
                  (<RemainingBudgetPercentagePill value={remainingPct} />)
                </Typography>
              )}
            </Stack>
          </TableCell>
        )}
        {isCol('remaining_per_day') && <TableCell><WholeDollarFormatter value={row.calendar_budget_remaining_per_day} /></TableCell>}
        {isCol('yesterday_spend') && (
          <TableCell>
            <HighlightIfZero value={row.yesterday_spend}>
              <WholeDollarFormatter value={row.yesterday_spend} />
            </HighlightIfZero>
          </TableCell>
        )}
        {isCol('exhaustion_date') && (
          <TableCell>{isTotal ? '-' : <ExhaustionDatePill row={row} />}</TableCell>
        )}
        {isCol('fb_budget') && <TableCell><WholeDollarFormatter value={row.facebook_daily_budget_total} /></TableCell>}
        {isCol('fb_pace') && <TableCell><PacingPill value={facebookBudgetPace} /></TableCell>}
        {isCol('leads') && <TableCell>{row.total_leads_sum}</TableCell>}
        {isCol('cpl') && <TableCell><WholeDollarFormatter value={row.cost_per_lead_avg} /></TableCell>}
        {isCol('accepted_rate') && <TableCell><AcceptedRatePill value={acceptedRate} /></TableCell>}
        {isCol('out_rate') && <TableCell><OutOfAreaRatePill value={outOfAreaRate} /></TableCell>}
        {isCol('self_books') && <TableCell>{row.self_books_sum}</TableCell>}
        {isCol('cp_self_book') && <TableCell><WholeDollarFormatter value={row.cost_per_self_book_avg} /></TableCell>}
        {isCol('self_book_rate') && <TableCell><PercentageFormatter value={selfBookRate} /></TableCell>}
        {isCol('booked') && <TableCell>{row.booked_sum}</TableCell>}
        {isCol('scheduled') && <TableCell>{row.scheduled_sum}</TableCell>}
        {isCol('issues') && <TableCell>{row.issues_sum}</TableCell>}
        {isCol('demos') && <TableCell>{row.demos_sum}</TableCell>}
        {isCol('gross_sales') && <TableCell>{row.gross_sales_sum}</TableCell>}
        {isCol('gross_amount') && <TableCell><WholeDollarFormatter value={row.gross_amount_sum} /></TableCell>}
        {isCol('net_sales') && <TableCell>{row.net_sales_sum}</TableCell>}
        {isCol('net_amount') && <TableCell><WholeDollarFormatter value={row.net_amount_sum} /></TableCell>}
      </React.Fragment>
    );
  };

  const visibleColCount = COLUMNS.filter((c) => isCol(c.id)).length;

  return (
    <Box p={3}>
      <Card>
        <CardHeader
          title="Client Pulse"
          action={
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1, mt: 1 }}>
              <kbd style={{ fontSize: '0.75rem', padding: '1px 4px', border: '1px solid', borderRadius: 3, borderColor: 'inherit' }}>R</kbd> refresh
              {' '}<kbd style={{ fontSize: '0.75rem', padding: '1px 4px', border: '1px solid', borderRadius: 3, borderColor: 'inherit' }}>/</kbd> search
            </Typography>
          }
        />
        <Divider />
        <CardContent>
          {/* Toolbar row 1: Date range + presets + refresh */}
          <Stack direction="row" spacing={2} mb={1.5} alignItems="center" flexWrap="wrap">
            <Box>
              <Typography variant="caption" fontWeight={500} mb={0.5} display="block">
                Date Range
              </Typography>
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={handleDateRangeChange}
                style={{ minWidth: 220 }}
              />
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="flex-end" sx={{ pb: 0.25 }}>
              {DATE_PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  size="small"
                  variant="text"
                  sx={{ textTransform: 'none', minWidth: 'auto', px: 1, fontSize: '0.75rem' }}
                  onClick={() => handleDatePreset(preset)}
                >
                  {preset.label}
                </Button>
              ))}
            </Stack>
            <Stack spacing={0.25}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleRefresh}
                disabled={isLoading}
                startIcon={
                  isLoading ? <CircularProgress size={14} /> : <Refresh fontSize="small" />
                }
              >
                Refresh
              </Button>
              <Typography variant="caption" color="text.secondary">
                {lastRefreshedLabel}
              </Typography>
            </Stack>
          </Stack>

          {/* Toolbar row 2: Search + filters + column picker + parent links */}
          <Stack direction="row" spacing={2} mb={2} alignItems="center" flexWrap="wrap">
            <TextField
              inputRef={searchRef}
              size="small"
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 200, maxWidth: 280 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Box sx={{ minWidth: 220 }}>
              <FormControl size="small" fullWidth>
                <InputLabel id="client-status-filter-label">Client Status</InputLabel>
                <Select
                  labelId="client-status-filter-label"
                  multiple
                  value={resolvedStatusFilter}
                  onChange={handleStatusFilterChange}
                  input={<OutlinedInput label="Client Status" />}
                  renderValue={renderStatusValue}
                  MenuProps={MULTI_SELECT_MENU_PROPS}
                >
                  {CLIENT_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Checkbox checked={resolvedStatusFilter.indexOf(option.value) > -1} />
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ minWidth: 220 }}>
              <FormControl size="small" fullWidth>
                <InputLabel id="client-tier-filter-label">Client Tier</InputLabel>
                <Select
                  labelId="client-tier-filter-label"
                  multiple
                  value={resolvedTierFilter}
                  onChange={handleTierFilterChange}
                  input={<OutlinedInput label="Client Tier" />}
                  renderValue={renderTierValue}
                  MenuProps={MULTI_SELECT_MENU_PROPS}
                >
                  {tierOptions.length === 0 ? (
                    <MenuItem disabled value="">
                      <ListItemText primary="No tiers available" />
                    </MenuItem>
                  ) : (
                    tierOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Checkbox checked={resolvedTierFilter.indexOf(option.value) > -1} />
                        <ListItemText primary={option.label} />
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>
            <Tooltip title="Choose columns">
              <IconButton size="small" onClick={(e) => setColPickerAnchor(e.currentTarget)}>
                <ViewColumnIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Popover
              open={Boolean(colPickerAnchor)}
              anchorEl={colPickerAnchor}
              onClose={() => setColPickerAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              PaperProps={{ sx: { maxHeight: 400, width: 260, p: 1 } }}
            >
              <Typography variant="subtitle2" sx={{ px: 1, pb: 0.5 }}>Visible Columns</Typography>
              <Button size="small" sx={{ textTransform: 'none', mb: 0.5 }} onClick={() => { setVisibleCols(new Set(DEFAULT_VISIBLE_COLS)); persistVisibleColumns(DEFAULT_VISIBLE_COLS); }}>
                Reset to default
              </Button>
              {COLUMNS.map((col) => (
                <MenuItem key={col.id} dense disabled={col.locked} onClick={() => !col.locked && toggleColumn(col.id)}>
                  <Checkbox size="small" checked={isCol(col.id)} disabled={col.locked} sx={{ p: 0.25, mr: 1 }} />
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{col.label}</ListItemText>
                </MenuItem>
              ))}
            </Popover>
            {parentDashboardOptions.length > 0 ? (
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{ ml: 'auto', maxWidth: '100%', overflowX: 'auto', pb: 0.25, '& > *': { flexShrink: 0 } }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Parent Dashboards
                </Typography>
                {parentDashboardOptions.map((option) => (
                  <Button key={option.value} component={RouterLink} href={option.value} target="_blank" rel="noopener noreferrer" size="small" variant="outlined" sx={{ whiteSpace: 'nowrap' }}>
                    {option.label}
                  </Button>
                ))}
              </Stack>
            ) : null}
          </Stack>

          {/* End-of-month pacing alert banner */}
          {(() => {
            const daysLeft = getDaysLeftInMonth();
            if (daysLeft > 7 || isLoading || visibleClientRows.length === 0) return null;
            const atRiskClients = visibleClientRows.filter((row) => {
              const pacing = Number(row?.pacing_percentage);
              const exceedsBudget = Number.isFinite(pacing) && pacing > 110;
              const exhaustsEarly = isExhaustionBeforeMonthEnd(row);
              return exceedsBudget || exhaustsEarly;
            });
            if (atRiskClients.length === 0) return null;
            const names = atRiskClients.slice(0, 5).map((r) => r.client__name).join(', ');
            const extra = atRiskClients.length > 5 ? ` +${atRiskClients.length - 5} more` : '';
            return (
              <Alert
                severity="warning"
                icon={<WarningAmberIcon fontSize="small" />}
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {atRiskClients.length} client{atRiskClients.length !== 1 ? 's' : ''} projected to exceed budget or exhaust budget before month end
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {names}{extra} — {daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining in month
                </Typography>
              </Alert>
            );
          })()}

          <TableContainer component={Paper} sx={tableContainerSx}>
            <Table size="medium" stickyHeader sx={mainTableSx}>
              <TableHead>
                <TableRow>
                  {COLUMNS.filter((c) => isCol(c.id)).map((col) =>
                    col.sortField
                      ? renderSortableHeader(col.label, col.sortField, {
                          key: col.id,
                          sx: col.id === 'client' ? stickyClientHeaderSx : compactHeaderSx,
                        })
                      : (
                        <TableCell
                          key={col.id}
                          sx={col.id === 'ad_accounts' ? clientAccountCellSx : compactHeaderSx}
                        >
                          {col.label}
                        </TableCell>
                      )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Totals row — pinned at top */}
                {totalsRow && !isLoading ? (
                  <TableRow
                    key="totals"
                    sx={(theme) => ({
                      backgroundColor: safeAlpha(theme.palette.info.light, 0.12),
                      '& .MuiTableCell-root': { fontWeight: 700 },
                      position: 'sticky',
                      top: 56,
                      zIndex: 2,
                    })}
                  >
                    {renderDataCells(totalsRow, { isTotal: true })}
                  </TableRow>
                ) : null}

                {/* Skeleton loading */}
                {isLoading && rows.length === 0 ? (
                  Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                    <TableRow key={`skeleton-${i}`}>
                      {COLUMNS.filter((c) => isCol(c.id)).map((col) => (
                        <TableCell key={col.id}>
                          <Skeleton variant="text" width={col.id === 'client' ? 140 : 60} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : null}

                {/* Data rows */}
                {!isLoading || rows.length > 0 ? visibleClientRows.map((row) => {
                  const clientId = row.client_id;
                  const isExpanded = expandedClients.has(clientId);
                  const { channels, verticals } = isExpanded ? getClientChildren(clientId) : { channels: [], verticals: [] };
                  const isChildrenLoading = isExpanded && channels.length === 0 && verticals.length === 0;

                  return (
                    <React.Fragment key={row.uuid}>
                      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleClientExpanded(row)}>
                        {renderDataCells(row)}
                      </TableRow>

                      {/* Expanded channel sub-rows */}
                      {isExpanded && isChildrenLoading ? (
                        <TableRow>
                          <TableCell colSpan={visibleColCount} sx={{ py: 1, pl: 6 }}>
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                            <Typography variant="caption" color="text.secondary">Loading channels...</Typography>
                          </TableCell>
                        </TableRow>
                      ) : null}

                      {isExpanded && channels.map((channel) => (
                        <TableRow
                          key={channel.uuid || `ch-${channel.channel_id}`}
                          sx={(theme) => ({
                            backgroundColor: safeAlpha(theme.palette.action.hover, 0.04),
                            '& .MuiTableCell-root': { fontSize: '0.82rem' },
                          })}
                        >
                          {renderDataCells(channel, { indent: 1, label: channel.channel__name || channel.channel || 'Channel' })}
                        </TableRow>
                      ))}

                      {isExpanded && verticals.map((vertical) => (
                        <TableRow
                          key={vertical.uuid || `v-${vertical.vertical}`}
                          sx={(theme) => ({
                            backgroundColor: safeAlpha(theme.palette.action.hover, 0.08),
                            '& .MuiTableCell-root': { fontSize: '0.82rem', fontStyle: 'italic' },
                          })}
                        >
                          {renderDataCells(vertical, { indent: 2, label: vertical.vertical || 'Vertical' })}
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                }) : null}

                {!isLoading && visibleClientRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={visibleColCount} align="center">
                      {searchQuery ? 'No clients match your search.' : 'No data found.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Page {Math.max(clientPage, 1)} of {totalPages}
              {searchQuery ? ` (${visibleClientRows.length} shown)` : ''}
            </Typography>
            <TablePagination
              component="div"
              count={clientTotalCount}
              page={Math.max(0, clientPage - 1)}
              onPageChange={handlePageChange}
              rowsPerPage={clientPageSize}
              onRowsPerPageChange={handlePageSizeChange}
              rowsPerPageOptions={[25, 50, 100, 200]}
            />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
const getHoverBackground = (theme, opacity = 0.08) => {
  const hoverSource =
    theme?.palette?.primary?.main ||
    theme?.palette?.primary?.light ||
    theme?.palette?.secondary?.main ||
    theme?.palette?.text?.primary ||
    '#000000';
  return safeAlpha(hoverSource, opacity);
};
